import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { validate as validateUUID } from 'uuid';

import { updateCurrentIncident } from 'actions/diceActions';
import {
  useAllIncidents,
  useCreateIncident,
} from 'components/IAP/hooks/incidentHooks';
import {
  addUsersToIncidentRoster,
  getPositionsForIncident,
} from 'hooks/incident/useRoster';
import { rosterNodesForCreate } from './draftRosterModel';
import { useApplyPlanLibraryPlan } from 'hooks/planLibrary/planLibraryHooks';
import { useSetIncidentRelatedEvents } from 'hooks/orgEvents/orgEventHooks';
import { useUpdateV1IncidentMutation } from 'hooks/v1Incident/useUpdateV1Incident';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import {
  useHwcgFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';

import { registerIncidentLifecycleTourApi } from '../incidentLifecycleTour/incidentLifecycleTourBridge';
import {
  INCIDENT_TOUR_PARAM,
  incidentTourWorkspaceHref,
  wantsIncidentTour,
} from '../incidentLifecycleTour/incidentLifecycleTourSteps';
import { resolveIncidentTourWorkspaceHref } from '../incidentLifecycleTour/incidentTourTargets';
import { registerHwcgTourApi } from '../hwcgTour/hwcgTourBridge';
import {
  HWCG_TOUR_PARAM,
  hwcgTourTasksHref,
  resolveHwcgTourRosterHref,
  resolveHwcgTourTasksHref,
  wantsHwcgTour,
} from '../hwcgTour/hwcgTourSteps';
import { registerFocIncidentsTourApi } from 'components/MapPage/focIncidentsTour/focIncidentsTourBridge';
import { FOC_INCIDENTS_TOUR_PARAM } from 'components/MapPage/focIncidentsTour/focIncidentsTourSteps';
import { CreateIncidentWizard } from './CreateIncidentWizard';
import { hwcgGroupNoticePayload } from './hwcgEocNotice';
import {
  resolvePositionAssignments,
  type ActivatedUserPayload,
  type CreateIncidentFormValues,
  type CreateIncidentPrefill,
} from './createIncidentModel';

/**
 * Full-screen route for creating an incident. Prefill arrives as router state
 * so callers that used to pass props to the old dialog keep working.
 */
export default function CreateIncidentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const currentGroup = useAppSelector(selectGroup);
  const hwcg = useHwcgFlag();
  const focRequirements = useUscgFocRequirementsFlag();

  const [submitting, setSubmitting] = useState(false);

  const createIncident = useCreateIncident();
  const applyPlan = useApplyPlanLibraryPlan();
  const updateIncident = useUpdateV1IncidentMutation();
  const setRelatedEvents = useSetIncidentRelatedEvents();

  // Memoized because the wizard's default form values are derived from it.
  const prefill = useMemo(
    () => (location.state ?? {}) as CreateIncidentPrefill,
    [location.state]
  );

  // Incident Lifecycle tour: after the wizard submits, continue in the new
  // workspace. resolveWorkspaceHref is only the fallback if create is blocked.
  // The ICS incident list carries `escalated_at`; the Redux list is the
  // fallback while it loads.
  const currentIncident = useAppSelector((state) => state.app.currentIncident);
  const incidents = useAppSelector((state) => state.app.incidents);
  const allIncidents = useAllIncidents();
  const workspaceHref = resolveIncidentTourWorkspaceHref(
    currentIncident?.id ? String(currentIncident.id) : undefined,
    allIncidents.data ??
      (incidents ?? []).map((incident) => ({
        id: String(incident.id),
        archived: incident.archived,
      }))
  );
  const workspaceHrefRef = useRef(workspaceHref);
  workspaceHrefRef.current = workspaceHref;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const wantsTour = wantsIncidentTour(
    searchParams.get(INCIDENT_TOUR_PARAM),
    'wizard'
  );
  const wantsHwcgWizard =
    hwcg && wantsHwcgTour(searchParams.get(HWCG_TOUR_PARAM), 'wizard');
  const tourStartedRef = useRef(false);
  const focIncidentsTourStartedRef = useRef(false);
  const createdDuringTourRef = useRef(false);
  const createdDuringHwcgTourRef = useRef(false);
  const wantsFocIncidentsTour =
    searchParams.get(FOC_INCIDENTS_TOUR_PARAM) === '1';
  if (wantsHwcgWizard) {
    createdDuringHwcgTourRef.current = true;
  } else if (wantsTour) {
    createdDuringTourRef.current = true;
  }

  const tasksHref = resolveHwcgTourTasksHref(
    currentIncident?.id ? String(currentIncident.id) : undefined,
    allIncidents.data ??
      (incidents ?? []).map((incident) => ({
        id: String(incident.id),
        archived: incident.archived,
      }))
  );
  const tasksHrefRef = useRef(tasksHref);
  tasksHrefRef.current = tasksHref;
  const rosterHref = resolveHwcgTourRosterHref(
    currentIncident?.id ? String(currentIncident.id) : undefined,
    allIncidents.data ??
      (incidents ?? []).map((incident) => ({
        id: String(incident.id),
        archived: incident.archived,
      }))
  );
  const rosterHrefRef = useRef(rosterHref);
  rosterHrefRef.current = rosterHref;

  useEffect(
    () =>
      registerIncidentLifecycleTourApi({
        navigateTo: (href) => navigateRef.current(href),
        resolveWorkspaceHref: () => workspaceHrefRef.current,
      }),
    []
  );

  useEffect(
    () =>
      registerHwcgTourApi({
        navigateTo: (href) => navigateRef.current(href),
        resolveRosterHref: () => rosterHrefRef.current,
        resolveTasksHref: () => tasksHrefRef.current,
      }),
    []
  );

  useEffect(() => {
    return registerFocIncidentsTourApi({
      getLocation: () => ({
        pathname: location.pathname,
        search: location.search,
      }),
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!wantsFocIncidentsTour) {
      focIncidentsTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focIncidentsTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      focIncidentsTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_INCIDENTS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focIncidentsTour/focIncidentsTour')
        .then(({ consumeFocIncidentsTourResume, startFocIncidentsTour }) => {
          const resume = consumeFocIncidentsTourResume();
          startFocIncidentsTour({ startId: resume ?? 'inc003' });
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    focRequirements,
    setSearchParams,
    wantsFocIncidentsTour,
  ]);

  useEffect(() => {
    if (wantsHwcgWizard) {
      if (tourStartedRef.current) return;
      const timer = window.setTimeout(() => {
        if (tourStartedRef.current) return;
        tourStartedRef.current = true;
        setSearchParams(
          (params) => {
            params.delete(HWCG_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        void import('../hwcgTour/hwcgTour')
          .then(({ startHwcgTour }) => {
            startHwcgTour('wizard', true);
          })
          .catch(() => {});
      }, 400);
      return () => window.clearTimeout(timer);
    }

    if (!wantsTour || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(INCIDENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../incidentLifecycleTour/incidentLifecycleTour')
        .then(({ startIncidentLifecycleTour }) => {
          startIncidentLifecycleTour('wizard');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsHwcgWizard, wantsTour, setSearchParams]);

  const close = () => {
    const historyIndex =
      typeof window.history.state?.idx === 'number'
        ? window.history.state.idx
        : 0;
    // React Router 7 assigns a key even on a fresh load, so key!=='default' is
    // not enough to know there is a prior app entry. idx<=0 means this is the
    // first history entry and navigate(-1) would no-op.
    if (location.key === 'default' || historyIndex <= 0) {
      navigate('/hub');
      return;
    }
    navigate(-1);
  };

  const onSubmit = async (
    values: CreateIncidentFormValues,
    activatedUsers: ActivatedUserPayload[]
  ) => {
    setSubmitting(true);
    try {
      const incident = await createIncident.mutateAsync({
        name: values.incident_name,
        group_guid: values.sponsor_organization,
        areas_of_responsibility: values.areas_of_responsibility.filter((id) =>
          validateUUID(id)
        ),
        incident_types: values.incident_types,
        ics_form_family: values.ics_form_family,
        activated_users: activatedUsers,
        activation_channels: values.activation_channels,
        activation_message: values.activation_message.trim() || undefined,
        roster_nodes:
          values.roster_seats?.length > 0
            ? rosterNodesForCreate(values.roster_seats)
            : undefined,
        ...(hwcg
          ? {
              hwcg_roster_externals: values.hwcg_roster_externals,
              hwcg_group_actions: values.hwcg_group_actions,
              hwcg_responsible_party: values.hwcg_specifics?.companyName ?? '',
              hwcg_authorization: {
                ...values.hwcg_authorization,
                date:
                  values.hwcg_authorization.date || values.hwcg_specifics.date,
                time:
                  values.hwcg_authorization.time || values.hwcg_specifics.time,
              },
              ...(values.hwcg_group_actions.eoc === 'activate' ||
              values.hwcg_group_actions.eoc === 'notify'
                ? {
                    hwcg_eoc: hwcgGroupNoticePayload(
                      'eoc',
                      values.hwcg_group_actions.eoc,
                      values.hwcg_eoc,
                      values.hwcg_specifics
                    ),
                  }
                : {}),
              hwcg_group_notices: {
                ...(values.hwcg_group_actions.responsible_party ===
                  'activate' ||
                values.hwcg_group_actions.responsible_party === 'notify'
                  ? {
                      responsible_party: hwcgGroupNoticePayload(
                        'responsible_party',
                        values.hwcg_group_actions.responsible_party,
                        values.hwcg_notices.responsible_party,
                        values.hwcg_specifics
                      ),
                    }
                  : {}),
                ...(values.hwcg_group_actions.mutual_aid === 'notify'
                  ? {
                      mutual_aid: hwcgGroupNoticePayload(
                        'mutual_aid',
                        'notify',
                        values.hwcg_notices.mutual_aid,
                        values.hwcg_specifics
                      ),
                    }
                  : {}),
                ...(values.hwcg_group_actions.service_provider === 'notify'
                  ? {
                      service_provider: hwcgGroupNoticePayload(
                        'service_provider',
                        'notify',
                        values.hwcg_notices.service_provider,
                        values.hwcg_specifics
                      ),
                    }
                  : {}),
              },
            }
          : {}),
      });

      if (!incident?.id) return;

      await Promise.allSettled([
        persistIncidentDetails(incident, values),
        seatPeople(incident.id, values),
        linkRelatedEvents(incident.id, values),
      ]);

      if (values.plan_id) {
        try {
          await applyPlan.mutateAsync({
            id: values.plan_id,
            incidentId: incident.id,
          });
        } catch {
          // The hook already toasts. The incident still opens.
        }
      }

      dispatch(updateCurrentIncident(incident));
      const dispatchedNotices =
        hwcg &&
        Object.values(values.hwcg_group_actions ?? {}).some(
          (action) => action === 'activate' || action === 'notify'
        );
      const dest = createdDuringHwcgTourRef.current
        ? hwcgTourTasksHref(incident.id)
        : createdDuringTourRef.current
          ? incidentTourWorkspaceHref(incident.id)
          : dispatchedNotices
            ? `/iap/${incident.id}/group-actions`
            : hwcg
              ? `/iap/${incident.id}/roster`
              : `/iap/${incident.id}/initial-response`;
      if (dispatchedNotices && !createdDuringHwcgTourRef.current) {
        toast.success(
          'Incident created. Group Actions shows who replied to each notice.'
        );
      }
      navigate(dest);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * The create endpoint takes neither a location nor settings, so the picker's
   * geometry and the HWCG intake are saved right after. A failure here must not
   * strand the new incident.
   */
  const persistIncidentDetails = async (
    incident: { id: string; name: string; settings?: Record<string, any> },
    values: CreateIncidentFormValues
  ) => {
    const hasLocation = !!values.location || !!values.address;
    if (!hasLocation && !hwcg) return;
    try {
      await updateIncident.mutateAsync({
        v1IncidentId: incident.id,
        name: incident.name,
        incidentTypes: values.incident_types,
        location: values.location
          ? (values.location as unknown as Record<string, any>)
          : null,
        address: values.address,
        ...(hwcg
          ? {
              settings: {
                ...(incident.settings ?? {}),
                hwcg: {
                  ...(incident.settings?.hwcg ?? {}),
                  specifics: values.hwcg_specifics,
                  groupActions: values.hwcg_group_actions,
                  authorization: values.hwcg_authorization,
                  eoc: values.hwcg_eoc,
                  notices: values.hwcg_notices,
                },
              },
            }
          : {}),
      });
    } catch {
      toast.warn(
        'Incident created, but its details could not be saved. Set them from incident settings.'
      );
    }
  };

  /** Seat assignees from the draft chart once the incident org chart exists. */
  const seatPeople = async (
    incidentId: string,
    values: CreateIncidentFormValues
  ) => {
    const seated = (values.people ?? []).filter((person) => person.position);
    if (seated.length === 0) return;
    try {
      const positions = await getPositionsForIncident(incidentId);
      const positionAssignments = resolvePositionAssignments(seated, positions);
      if (positionAssignments.length === 0) return;
      await addUsersToIncidentRoster({
        incidentId,
        groupGuid:
          values.sponsor_organization || currentGroup?.group_guid || '',
        positionAssignments,
        hwcgActivationNotice: hwcg,
      });
    } catch {
      toast.warn(
        'Incident created, but ICS seats could not be assigned. Assign them from the org chart.'
      );
    }
  };

  const linkRelatedEvents = async (
    incidentId: string,
    values: CreateIncidentFormValues
  ) => {
    const eventIds = (values.related_events ?? []).filter((id) =>
      validateUUID(id)
    );
    if (eventIds.length === 0) return;
    try {
      await setRelatedEvents.mutateAsync({ incidentId, eventIds });
    } catch (error) {
      console.error('Failed to link related events to incident', error);
    }
  };

  return (
    <CreateIncidentWizard
      prefill={prefill}
      submitting={submitting}
      onSubmit={(values, activatedUsers) =>
        void onSubmit(values, activatedUsers)
      }
      onClose={close}
    />
  );
}
